import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface OrderItemCreate {
  product_id: number;
  quantity: number;
  size_id?: number;
  unit_price?: number;
  selected_option_item_ids?: number[];
  // task-191: cc_option_items ids (Cake Options). Validated and priced
  // server-side; unit_price is never trusted.
  selected_cc_option_ids?: number[];
  // URL returned by POST /orders/custom-image (customizable products only;
  // omit when no photo). Mirrors OrderItemCreate.custom_image_url (max 500).
  custom_image_url?: string;
}

export interface OrderCreate {
  pickup_date: string;  // Format: YYYY-MM-DD
  pickup_time: string;  // Format: HH:MM:SS
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  special_instructions?: string;
  items?: OrderItemCreate[];  // Optional cart items
  byo_amount?: number;  // display/legacy-only -- not trusted once byo_order_id is set
  byo_order_id?: number;
  // Mirrors backend/app/schemas/order.py's OrderCreate.redeem_points -- customer
  // opt-in to redeem their points balance against this order.
  redeem_points?: boolean;
}

export interface OrderResponse {
  order_id: number;
  order_number: string;
  status: string;
  total_amount: number;
  pickup_date: string;
  pickup_time: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  special_instructions?: string;
  created_at: string;
  // Mirrors backend/app/schemas/order.py's OrderResponse.points_earned/
  // points_redeemed/points_redeemed_value -- always populated (non-nullable,
  // default 0/0/0.0) once an Order row exists, matching auth.ts's
  // loyalty_points_balance convention for non-nullable model columns.
  points_earned: number;
  points_redeemed: number;
  points_redeemed_value: number;
  // Lifetime-discount snapshot (0/absent for guests and 0% customers).
  lifetime_discount_percent?: number;
  lifetime_discount_amount?: number;
}

// A single BYO/customizable-product option selection, snapshotted onto the
// order item at checkout time. Mirrors
// backend/app/schemas/order.py's OrderItemResponse.selected_options entries.
export interface SelectedOption {
  category_id: number;
  category_name: string | null;
  category_name_zh: string | null;
  item_id: number;
  item_name: string;
  item_name_zh: string | null;
  price_modifier: number;
  // 'cc' for Cake Option (cc_* group) selections (task-191); absent for legacy BYO-style ones.
  source?: string;
}

export interface OrderItem {
  order_item_id: number;
  product_id: number;
  size_id: number | null;
  quantity: number;
  unit_price: number;
  subtotal: number;
  // Populated for customizable products; null/undefined when the item has
  // no customization selections (regular non-customizable products, or
  // orders placed before this feature existed).
  selected_options?: SelectedOption[] | null;
  // Customer-uploaded cake photo URL (customizable products); null/absent otherwise.
  custom_image_url?: string | null;
  // Server-computed from OrderItem.product (backend/app/db/models.py) at
  // read time -- always reflects the product's current name/name_zh even
  // once the product has been deactivated (never hard-deleted once it has
  // order history), unlike the live /products/{id} catalog fetch which
  // filters active==True and 404s for a deactivated product.
  product_name?: string | null;
  product_name_zh?: string | null;
}

export interface OrderDetail extends OrderResponse {
  items: OrderItem[];
}

export interface CustomImageUploadResponse {
  url: string;
}

export const ordersAPI = {
  // Upload a customer's cake photo (public endpoint, no auth). Returns the
  // app-issued URL to carry on the cart line / OrderItemCreate.custom_image_url.
  uploadCustomCakeImage: async (file: File): Promise<CustomImageUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post<CustomImageUploadResponse>(
      `${API_BASE}/orders/custom-image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Create a new order
  createOrder: async (orderData: OrderCreate): Promise<OrderResponse> => {
    const token = localStorage.getItem('access_token');
    const response = await axios.post(`${API_BASE}/orders/`, orderData, {
      withCredentials: true,  // Include cookies for session
      headers: token ? {
        Authorization: `Bearer ${token}`
      } : {}
    });
    return response.data;
  },

  // Get order details
  getOrderDetail: async (orderId: number): Promise<OrderDetail> => {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(`${API_BASE}/orders/${orderId}`, {
      headers: token ? {
        Authorization: `Bearer ${token}`
      } : {}
    });
    return response.data;
  },

  // Get user's orders (requires authentication)
  getUserOrders: async (): Promise<OrderResponse[]> => {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(`${API_BASE}/orders/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  // Link an existing order to the current authenticated user
  linkOrderToAccount: async (orderId: number): Promise<OrderResponse> => {
    const token = localStorage.getItem('access_token');
    const response = await axios.put(`${API_BASE}/orders/${orderId}/link`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
};